import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao } from '../../componentes/base';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Calendario, intervaloDe, type EventoCalendario, type VisaoCalendario } from '../../componentes/calendario';
import { Erro } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { diasDoDiaInteiro, somarDiasIso } from '../../componentes/reuniao-livre';
import { useSessao } from '../../nucleo/sessao';
import { coresDaAgenda, corSoltaDoRecurso } from './cores';
import { TextoCortado } from '../../componentes/texto-cortado';
import { deDiaLocal, PAGINAS_AGENDA, useAgendaAoVivo, useAgendamentos, useBloqueios, useRecursos } from './dados';
import { EventoDoGoogle } from './EventoDoGoogle';
import { DetalheAgendamento } from './DetalheAgendamento';
import { FormReuniaoLivre } from './FormReuniaoLivre';
import { NovoAgendamento } from './NovoAgendamento';
import { ehReuniaoLivre, type Agendamento } from './tipos';
import { nomeCadastro, nomeParceiro, nomePessoa, numero, textoLivre } from '../../nucleo/formato';

/** Dia inteiro no calendário: os dias do fuso do workspace, na meia-noite do navegador em que a grade divide os dias. */
function diaInteiroNoCalendario(a: Agendamento, fuso: string) {
  if (!a.dia_inteiro) return {};
  const { dia, diaFinal } = diasDoDiaInteiro(a, fuso);
  return { inicio: deDiaLocal(dia).toISOString(), fim: deDiaLocal(somarDiasIso(diaFinal, 1)).toISOString(), diaInteiro: true };
}

/** Ficha 2026-11-03 (item 5): pessoas na legenda, para a linha do filtro não virar um bloco. */
const LEGENDA_MAX = 12;

/**
 * Semana, dia e mês: o componente Calendario do núcleo com filtro por recurso. O botão de criar e clicar num horário abrem a
 * reunião livre já naquele dia e hora (ficha 2026-11-03, decisão 1); "Agendar serviço" abre o agendamento com serviço e cliente,
 * dos fluxos por serviço. Clicar num evento abre o drawer com as ações de estado e o chatter. `?data=AAAA-MM-DD` abre
 * numa data e `?agendamento=<id>` abre o drawer de um agendamento (usado pelo link da ficha do cliente).
 */
export function VisaoAgenda({
  visao,
  aoNovo,
  aoEditar,
  semAcaoPrimaria,
  reuniaoCrm,
}: {
  visao: VisaoCalendario;
  /** Lote crm8 (item 5): Reuniões/Agenda do CRM abre o "Agendar reunião" pela página, com o botão do cabeçalho dela. */
  aoNovo?: (inicio: Date | null) => void;
  /** Lote crm8 (item 5): "Editar reunião" no detalhe (Reuniões/Agenda do CRM). */
  aoEditar?: ComponentProps<typeof DetalheAgendamento>['aoEditar'];
  /** Lote crm8 (item 5): a página que usa a visão já tem o botão primário no cabeçalho. */
  semAcaoPrimaria?: boolean;
  /** Rlote-10 (SPEC-M1): Reuniões/Agenda do CRM; o detalhe mostra as ações da reunião encerrada (decisão 9 de 27/10). */
  reuniaoCrm?: boolean;
}) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const [params, setParams] = useSearchParams();
  const [referencia, setReferencia] = useState(() => {
    const d = params.get('data');
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? deDiaLocal(d) : new Date();
  });
  // 03/11, pedido do Gustavo: abre na agenda da própria pessoa; nulo é "ainda não escolheu", e "" é Todos, escolhido por ela.
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(params.get('agendamento'));
  const [novo, setNovo] = useState<{ inicio: Date | null } | null>(null);
  const [agendarServico, setAgendarServico] = useState(false);
  const [editarLivre, setEditarLivre] = useState<Agendamento | null>(null);

  const recursos = useRecursos();
  // Ficha 2026-11-03: a cor de cada pessoa. Com os inativos, para a cor de ninguém mudar quando alguém é desativado.
  const todosRecursos = useRecursos(false);
  const cores = useMemo(() => coresDaAgenda(todosRecursos.data ?? []), [todosRecursos.data]);
  const { de, ate } = useMemo(() => intervaloDe(visao, referencia), [visao, referencia]);
  // r9 robustez B3: acima de 2.000 agendamentos no período a tela avisa e oferece carregar mais (antes cortava em silêncio).
  // r10 robustez B1: cada "Carregar mais" busca só o lote seguinte e acumula.
  const meuRecurso = recursos.data?.find((r) => r.usuario_id === s.usuario.id)?.id;
  const recursoId = escolhido ?? meuRecurso ?? '';
  // Sem a lista, ainda não se sabe qual é a agenda da pessoa: não busca a de todos para trocar em seguida.
  const pronto = escolhido !== null || !recursos.isPending;
  const ags = useAgendamentos(de, ate, recursoId, pronto);
  const bls = useBloqueios(de, ate, recursoId, pronto);
  useAgendaAoVivo();
  const [eventoGoogle, setEventoGoogle] = useState<string | null>(null);

  // O link `?agendamento=` vale uma vez: ao fechar o drawer a URL fica limpa.
  useEffect(() => {
    if (params.get('agendamento') && !aberto) {
      const p = new URLSearchParams(params);
      p.delete('agendamento');
      setParams(p, { replace: true });
    }
  }, [aberto, params, setParams]);

  const eventos = useMemo<EventoCalendario[]>(
    () => [
      ...ags.itens.map((a) => ({
        id: a.id,
        inicio: a.inicio,
        fim: a.fim,
        // Lote crm8 (item 5): a reunião do CRM aparece pelo assunto (o cliente vai para o subtítulo), como no Google.
        // Rlote-9 (VIS-M3): o assunto se mascara com todos os nomes da reunião (cliente e convidados), como no formulário.
        titulo: a.assunto ? textoLivre(a.assunto, a.parceiro_nome, ...(a.convidados ?? []).map((c) => c.nome)) : ehReuniaoLivre(a) ? t('crm.semAssunto') : nomeParceiro(a.parceiro_nome),
        // Reunião livre (03/11): sem serviço; o cliente só quando há vínculo (nulo aqui não é "Cadastro de outra empresa").
        subtitulo: ehReuniaoLivre(a)
          ? [a.parceiro_id && nomeParceiro(a.parceiro_nome), nomePessoa(nomeCadastro(a.recurso_nome)), t(`estados.${a.estado}`)].filter(Boolean).join(', ')
          : `${a.assunto ? `${nomeParceiro(a.parceiro_nome)}, ` : ''}${nomeCadastro(a.servico_nome)} (${nomeCadastro(a.recurso_nome)}), ${t(`estados.${a.estado}`)}${a.encaixe ? `, ${t('campos.encaixe')}` : ''}`,
        cor: a.recurso_cor ?? ('neutro' as const),
        corPessoa: cores.get(a.recurso_id) ?? corSoltaDoRecurso(a.recurso_cor_google, a.recurso_cor),
        ...diaInteiroNoCalendario(a, s.workspace.fuso),
      })),
      ...(bls.data ?? []).map((b) => ({
        id: `bloqueio:${b.id}`,
        inicio: b.inicio,
        fim: b.fim,
        // r11 visual M4 e observação da robustez: o motivo é texto livre (pode citar valor, documento, e-mail, telefone).
        // 03/11, pedido do Gustavo: sem o rótulo "Bloqueio" na frente; o nome da pessoa já vai no subtítulo. O nome do evento só vem à dona.
        titulo: b.titulo_google ? textoLivre(b.titulo_google) : b.motivo ? textoLivre(b.motivo) : t('bloqueios.ocupado'),
        subtitulo: b.recurso_nome,
        cor: 'neutro' as const,
        corPessoa: cores.get(b.recurso_id) ?? corSoltaDoRecurso(b.recurso_cor_google, b.recurso_cor),
        // Ficha 2026-11-03: só a dona abre o evento dela, com a mesma permissão da rota.
        estatico: !(b.meu_google && s.pode('agenda.criar')),
      })),
    ],
    [ags.itens, bls.data, t, s, cores],
  );
  // Ficha 2026-11-03 (item 5): quem aparece no período, com a cor do card. Com um recurso filtrado, não há o que distinguir.
  const legenda = useMemo(() => {
    if (recursoId) return [];
    const presentes = new Set<string>([...ags.itens.map((a) => a.recurso_id), ...(bls.data ?? []).map((b) => b.recurso_id)]);
    return (todosRecursos.data ?? []).filter((r) => presentes.has(r.id) && cores.has(r.id));
  }, [recursoId, ags.itens, bls.data, todosRecursos.data, cores]);

  // r11 visual B3: o "Carregar mais" do último lote some com o aviso; o foco ia para o body. Agora vai para a grade.
  // Review r12 visual B1: o requestAnimationFrame às vezes olhava o foco antes de o React tirar o botão (1 em 15 com a máquina
  // carregada). Agora o pedido fica marcado e o foco é decidido no commit em que o carregamento termina (layout effect, antes da
  // pintura): se o aviso sumiu com o botão, a grade recebe o foco; se ainda há mais, o foco fica no botão.
  const grade = useRef<HTMLDivElement>(null);
  const focoAposCarregar = useRef(false);
  const carregarMais = () => {
    focoAposCarregar.current = true;
    void ags.fetchNextPage();
  };
  useLayoutEffect(() => {
    if (!focoAposCarregar.current || ags.carregandoMais) return;
    focoAposCarregar.current = false;
    if (ags.parcial) return;
    const ativo = document.activeElement;
    if (!ativo || ativo === document.body || !ativo.isConnected) grade.current?.focus();
  }, [ags.parcial, ags.carregandoMais]);

  return (
    <div className="flex flex-col gap-4">
      {!semAcaoPrimaria && (s.pode('agenda.criar') || s.pode('agenda.excluir')) && (
        <AcaoPrimaria>
          {/* Fase 2 (W25, K21): agendamento cancelado excluído volta pela lixeira em até 30 dias. */}
          {s.pode('agenda.excluir') && <BotaoLixeira entidades={['agenda.agendamento']} />}
          {s.pode('agenda.criar') && (
            <Botao onClick={() => setAgendarServico(true)} data-agendar-servico="">
              {t('reuniaoLivre.agendarServico')}
            </Botao>
          )}
          {s.pode('agenda.criar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo({ inicio: null })} data-agendar-reuniao="">
              {t('crm.agendar')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-64">
          <Selecao aria-label={t('campos.recurso')} vazio={t('calendario.todosRecursos')} opcoes={(recursos.data ?? []).map((r) => ({ valor: r.id, rotulo: r.usuario_id ? nomePessoa(r.nome) : r.nome }))} value={recursoId} onChange={(e) => setEscolhido(e.target.value)} />
        </div>
        {legenda.length > 1 && (
          <ul aria-label={t('calendario.legenda')} data-legenda-agenda className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-tinta-2">
            {legenda.slice(0, LEGENDA_MAX).map((r) => (
              <li key={r.id} className="flex min-w-0 items-center gap-1.5">
                <span aria-hidden className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: cores.get(r.id) }} />
                <TextoCortado>{r.usuario_id ? nomePessoa(r.nome) : r.nome}</TextoCortado>
              </li>
            ))}
            {legenda.length > LEGENDA_MAX && <li>+{legenda.length - LEGENDA_MAX}</li>}
          </ul>
        )}
        {(ags.isFetching || bls.isFetching) && <span className="text-sm text-tinta-3">{tn('comum.carregando')}</span>}
      </div>
      {ags.parcial && (
        <div role="status" data-agenda-parcial className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-laranja px-3 py-2 text-sm text-tinta-1">
          <span className="min-w-0">{t('calendario.parcial', { n: numero(ags.itens.length) })}</span>
          <Botao tamanho="sm" variante="secundario" carregando={ags.carregandoMais} onClick={carregarMais}>
            {t('calendario.carregarMais', { n: numero(PAGINAS_AGENDA * 200) })}
          </Botao>
        </div>
      )}
      {ags.isError ? (
        <Erro erro={ags.error} aoTentar={() => ags.refetch()} />
      ) : (
        <div ref={grade} tabIndex={-1} data-grade-agenda aria-label={t('calendario.grade')} role="region" className="rounded-xl focus:outline-2 focus:outline-foco">
        <Calendario
          visao={visao}
          referencia={referencia}
          eventos={eventos}
          aoNavegar={setReferencia}
          aoClicarEvento={(e) => (e.id.startsWith('bloqueio:') ? setEventoGoogle(e.id.slice('bloqueio:'.length)) : setAberto(e.id))}
          aoClicarHorario={s.pode('agenda.criar') ? (inicio) => (aoNovo ? aoNovo(inicio) : setNovo({ inicio })) : undefined}
        />
        </div>
      )}
      {!aoNovo && novo && <FormReuniaoLivre inicio={novo.inicio} aoFechar={() => setNovo(null)} />}
      {!aoNovo && <NovoAgendamento aberto={agendarServico} inicial={null} aoFechar={() => setAgendarServico(false)} aoCriado={() => setAgendarServico(false)} />}
      {editarLivre && <FormReuniaoLivre atual={editarLivre} aoFechar={() => setEditarLivre(null)} />}
      {eventoGoogle && <EventoDoGoogle bloqueioId={eventoGoogle} aoFechar={() => setEventoGoogle(null)} />}
      <DetalheAgendamento
        id={aberto}
        aoFechar={() => setAberto(null)}
        reuniaoCrm={reuniaoCrm}
        aoEditar={
          aoEditar &&
          ((a) => {
            setAberto(null);
            aoEditar(a);
          })
        }
        aoEditarLivre={
          aoEditar
            ? undefined
            : (a) => {
                setAberto(null);
                setEditarLivre(a);
              }
        }
      />
    </div>
  );
}
